import { useEffect, useState } from 'react';
import { Modal, Alert, Form, Input, Radio, message } from 'antd';
import ExpertInfoCard from '@/components/common/ExpertInfoCard';
import { changeExpertCredibilityApi } from '@services/Experts';
import { logExpertChangeCredibility } from '@/utils/expertLogger';
import './ChangeCredibilityModal.less';

const CREDIBILITY_VALUES = ['high', 'medium', 'low'];

const RADIO_LABELS = {
    high: '高可信',
    medium: '中可信',
    low: '低可信',
};

function normalizeCredibility(value) {
    return CREDIBILITY_VALUES.includes(value) ? value : 'medium';
}

/**
 * 调整专家可信度弹窗（接口无调整理由字段，理由仅用于操作日志）
 */
export default function ChangeCredibilityModal({ open, expert, onCancel, onSuccess }) {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open || !expert) {
            return;
        }
        form.setFieldsValue({
            credibility: normalizeCredibility(expert.credibility),
            reason: undefined,
        });
    }, [open, expert, form]);

    const handleOk = async () => {
        if (!expert?.id) {
            return;
        }
        let values;
        try {
            values = await form.validateFields();
        } catch (e) {
            if (e?.errorFields) {
                return;
            }
            throw e;
        }

        setSubmitting(true);
        const fromCredibility = expert.credibility ?? null;
        const payload = {
            fromCredibility,
            toCredibility: values.credibility,
            adjustReason: values.reason,
        };

        try {
            const res = await changeExpertCredibilityApi({
                id: expert.id,
                credibility: values.credibility,
            });
            if (res?.status_code === 200) {
                message.success('已调整可信度');
                try {
                    await logExpertChangeCredibility(expert.id, payload, true);
                } catch (logErr) {
                    console.error('调整可信度操作日志上报失败', logErr);
                }
                form.resetFields();
                onSuccess?.();
            } else {
                try {
                    await logExpertChangeCredibility(expert.id, payload, false, res?.message || '调整可信度失败');
                } catch (_e) {
                    /* 忽略日志失败 */
                }
            }
        } catch (err) {
            const errMsg = typeof err === 'string' ? err : '调整可信度失败';
            try {
                await logExpertChangeCredibility(expert.id, payload, false, errMsg);
            } catch (_e) {
                /* 忽略日志失败 */
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel?.();
    };

    return (
        <Modal className="change-credibility-modal" title="调整可信度" open={open && !!expert} onCancel={handleCancel} onOk={handleOk} okText="确定" cancelText="取消" confirmLoading={submitting} destroyOnHidden width={560}>
            <Alert type="warning" title="可信度会影响数据使用者对专家参与项目的判断，请谨慎调整。" style={{ marginBottom: 10 }} />

            {expert ? (
                <div className="change-credibility-modal__summary">
                    <ExpertInfoCard expert={expert} showDetailButton={false} />
                </div>
            ) : null}

            <Form form={form} layout="vertical" preserve={false}>
                <Form.Item name="credibility" label="调整可信度" rules={[{ required: true, message: '请选择可信度' }]}>
                    <Radio.Group className="change-credibility-modal__radios">
                        {CREDIBILITY_VALUES.map(v => (
                            <Radio key={v} value={v} className={`change-credibility-modal__radio change-credibility-modal__radio--${v}`}>
                                {RADIO_LABELS[v]}
                            </Radio>
                        ))}
                    </Radio.Group>
                </Form.Item>
                <Form.Item name="reason" label="调整理由" rules={[{ required: true, message: '请填写调整理由' }]}>
                    <Input.TextArea rows={4} placeholder="填写手动调整可信度的理由" maxLength={500} showCount />
                </Form.Item>
            </Form>
        </Modal>
    );
}
